import { MUSIC_FADE_SECONDS } from './plan';
import { clipAt, clipLength, clipStarts, PHOTO_ZOOM, totalLength, voiceSpans, type OverlayTiming, type VideoProject } from './project';

/*
 * How dark the picture is and how loud the sound is at a moment of the finished video, from the fades (V2).
 * Pure: the preview (src/ui/videoView.ts, src/video/player.ts) and the export (src/video/exporter.ts) both use it,
 * so what plays on screen is what is written to the file.
 *
 *   joint dip ("จางผ่านสีดำ"): the clip before fades to black over its last FADE_SECONDS, the clip after fades in from
 *                              black over its first FADE_SECONDS — the video keeps its length. Text and pictures stay.
 *   start/end fade:            everything (pictures, text, sound) from/to black.
 *   voice (เสียงพากย์, 3ก):        while a scene's AI voice speaks, the music and the clips' own sound go down to
 *                              DUCK_LEVEL (over DUCK_RAMP seconds each way) and come back after it.
 */

/** music and clip sound while the voice speaks (times their own volume), and how long the change takes */
export const DUCK_LEVEL = 0.25;
export const DUCK_RAMP = 0.3;
/**
 * voices closer than this stay one dip: back-to-back narrated scenes are always VOICE_TAIL + VOICE_LEAD (0.6 s) apart,
 * and music swelling up and down in every such gap sounds like pumping
 */
export const DUCK_JOIN_SECONDS = 1.0;

/** the voice parts, joined where they are too close to come back up in between (a dip then stays down) */
function duckRuns(p: VideoProject): Array<[number, number]> {
  const runs: Array<[number, number]> = [];
  for (const v of voiceSpans(p)) {
    const last = runs[runs.length - 1];
    if (last && v.start - last[1] < DUCK_JOIN_SECONDS) last[1] = Math.max(last[1], v.end);
    else runs.push([v.start, v.end]);
  }
  return runs;
}

/** how loud the music and clips are at `t` because of the voice (1 = not lowered) */
export function duckAt(p: VideoProject, t: number): number {
  let level = 1;
  for (const [a, b] of duckRuns(p)) {
    let l = 1;
    if (t >= a && t <= b) l = DUCK_LEVEL;
    else if (t > a - DUCK_RAMP && t < a) l = 1 - (1 - DUCK_LEVEL) * (t - (a - DUCK_RAMP)) / DUCK_RAMP;
    else if (t > b && t < b + DUCK_RAMP) l = DUCK_LEVEL + (1 - DUCK_LEVEL) * (t - b) / DUCK_RAMP;
    level = Math.min(level, l);
  }
  return level;
}

/** the same as points for the export's mix (seconds → 0..1); the first point is set, the rest ramp to */
export function duckGainPoints(p: VideoProject): Array<[number, number]> {
  const pts: Array<[number, number]> = [[0, 1]];
  for (const [a, b] of duckRuns(p)) {
    const down = Math.max(0, a - DUCK_RAMP);
    if (down > pts[pts.length - 1][0]) pts.push([down, 1]);
    pts.push([Math.max(a, pts[pts.length - 1][0]), DUCK_LEVEL], [b, DUCK_LEVEL], [b + DUCK_RAMP, 1]);
  }
  return pts;
}

/** each side of a fade (seconds); a short clip uses at most half of itself for each side */
export const FADE_SECONDS = 0.5;
/** text and pictures fade in and out over this long (at most a third of the time they show) */
export const OVERLAY_FADE_SECONDS = 0.3;

export interface Dip {
  /** black over the clips only (joints), 0..1 */
  clip: number;
  /** black over everything (start/end of the video), 0..1 */
  all: number;
}

/** black at the start and end of clip `k` (0..1 each side) at `u` seconds into it */
function sides(p: VideoProject, k: number, u: number): { inA: number; outA: number } {
  const len = clipLength(p.clips[k]);
  const d = Math.min(FADE_SECONDS, len / 2);
  const inA = d > 0 && u < d ? 1 - u / d : 0;
  const outA = d > 0 && len - u < d ? 1 - Math.max(0, len - u) / d : 0;
  return { inA, outA };
}

/** how black the picture is at timeline time `t` */
export function dipAt(p: VideoProject, t: number): Dip {
  const at = clipAt(p, t);
  if (!at) return { clip: 0, all: 0 };
  const k = at.index, last = p.clips.length - 1;
  const u = Math.max(0, Math.min(t, totalLength(p)) - clipStarts(p)[k]);
  const { inA, outA } = sides(p, k, u);
  const fromStart = k === 0 ? (p.fadeIn ? inA : 0) : 0;
  const toEnd = k === last ? (p.fadeOut ? outA : 0) : 0;
  const jointIn = k > 0 && p.clips[k].fade ? inA : 0;
  const jointOut = k < last && p.clips[k + 1].fade ? outA : 0;
  return { clip: Math.max(jointIn, jointOut), all: Math.max(fromStart, toEnd) };
}

/** the music's fade in and out (seconds): with the picture at the start/end, or 2 s when the music is cut */
function musicFades(p: VideoProject): { fadeIn: number; fadeOut: number; total: number } {
  const total = totalLength(p);
  const first = p.clips[0], last = p.clips[p.clips.length - 1];
  const fadeIn = p.fadeIn && first ? Math.min(FADE_SECONDS, clipLength(first) / 2) : 0;
  const fadeOut = p.music && p.music.dur > total + 0.05 ? Math.min(MUSIC_FADE_SECONDS, total / 2)
    : p.fadeOut && last ? Math.min(FADE_SECONDS, clipLength(last) / 2) : 0;
  return { fadeIn, fadeOut, total };
}

/** gain points (seconds → 0..1, times the music's volume) for the export's mix; the first point is set, the rest ramp to */
export function musicGainPoints(p: VideoProject): Array<[number, number]> {
  const { fadeIn, fadeOut, total } = musicFades(p);
  const pts: Array<[number, number]> = [[0, fadeIn ? 0 : 1]];
  if (fadeIn) pts.push([fadeIn, 1]);
  if (fadeOut) { pts.push([Math.max(fadeIn, total - fadeOut), 1]); pts.push([total, 0]); }
  return pts;
}

/** the AI voice follows only the start/end fades of the whole video (it is not lowered at a joint dip) */
export function voiceGainPoints(p: VideoProject): Array<[number, number]> {
  const total = totalLength(p), first = p.clips[0], last = p.clips[p.clips.length - 1];
  const fi = p.fadeIn && first ? Math.min(FADE_SECONDS, clipLength(first) / 2) : 0;
  const fo = p.fadeOut && last ? Math.min(FADE_SECONDS, clipLength(last) / 2) : 0;
  const pts: Array<[number, number]> = [[0, fi ? 0 : 1]];
  if (fi) pts.push([fi, 1]);
  if (fo) { pts.push([Math.max(fi, total - fo), 1]); pts.push([total, 0]); }
  return pts;
}
export const voiceLevelAt = (p: VideoProject, t: number) => 1 - dipAt(p, t).all;

/** sound levels (0..1, times each clip's/the music's own volume) at timeline time `t` — the preview's mix */
export function levelsAt(p: VideoProject, t: number): { clip: number; music: number } {
  const d = dipAt(p, t);
  const { fadeIn, fadeOut, total } = musicFades(p);
  let music = 1;
  if (fadeIn && t < fadeIn) music = Math.min(music, t / fadeIn);
  if (fadeOut && t > total - fadeOut) music = Math.min(music, Math.max(0, total - t) / fadeOut);
  const duck = duckAt(p, t);   // under the AI voice (3ก)
  return { clip: (1 - Math.max(d.clip, d.all)) * duck, music: Math.max(0, music) * duck };
}

/**
 * gain points (seconds of the finished video → 0..1) that make clip `k`'s sound follow the fades, for an
 * OfflineAudioContext. Empty when the clip has no fade.
 */
export function clipGainPoints(p: VideoProject, k: number): Array<[number, number]> {
  const c = p.clips[k], last = p.clips.length - 1;
  const start = clipStarts(p)[k], len = clipLength(c);
  const d = Math.min(FADE_SECONDS, len / 2);
  const fadeIn = k === 0 ? p.fadeIn : c.fade;
  const fadeOut = k === last ? p.fadeOut : p.clips[k + 1].fade;
  if (!fadeIn && !fadeOut) return [];
  const pts: Array<[number, number]> = [];
  pts.push([start, fadeIn ? 0 : 1]);
  if (fadeIn) pts.push([start + d, 1]);
  if (fadeOut) { pts.push([start + len - d, 1]); pts.push([start + len, 0]); }
  return pts;
}

/** how visible a text/picture is at timeline time `t` (0 = not shown) */
export function overlayAlpha(o: OverlayTiming, t: number): number {
  if (t < o.vStart || t >= o.vEnd) return 0;
  if (!o.vFade) return 1;
  const d = Math.max(0.001, Math.min(OVERLAY_FADE_SECONDS, (o.vEnd - o.vStart) / 3));
  return Math.max(0, Math.min(1, (t - o.vStart) / d, (o.vEnd - t) / d));
}

/** a still picture's zoom `u` seconds into its `len` seconds (100% → PHOTO_ZOOM), same in preview and export */
export function photoZoom(u: number, len: number): number {
  return 1 + (PHOTO_ZOOM - 1) * Math.max(0, Math.min(1, len > 0 ? u / len : 0));
}
